import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetRecordApprovalsResult } from '@whinerp/client';
import { Button, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * The approval banner on a quote (F-049).
 *
 * `quote.status` has permitted 'pending-approval' since quotes shipped and
 * nothing ever set it; `approval.requested` is a notification kind nothing
 * sent; `approval.decide` and `discount.approve` are capabilities nothing
 * checked. This is where all four meet a person.
 *
 * The banner shows the outstanding request AND what was decided before it,
 * because the question asked six months later is "who agreed to this" and a
 * status on the quote answers none of it.
 *
 * Somebody looking at their own request sees why they cannot decide it rather
 * than an absent button. A control that vanishes for reasons a person cannot
 * see is a control they assume is broken.
 */
export function ApprovalBanner({ quoteId }: { quoteId: string }): JSX.Element | null {
  const t = useT();
  const { session, can } = useSession();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');

  const approvals = useQuery<GetRecordApprovalsResult, ApiError>({
    queryKey: ['approvals', 'quote', quoteId],
    queryFn: () => api.getRecordApprovals({ path: { objectKey: 'quote', recordId: quoteId } }),
  });

  const decide = useMutation<unknown, ApiError, { requestId: string; approve: boolean }>({
    mutationFn: ({ requestId, approve }) =>
      api.decideApproval({
        path: { requestId },
        body: { approve, comment: comment.trim() === '' ? null : comment.trim() },
      }),
    onSuccess: async () => {
      setComment('');
      await queryClient.invalidateQueries({ queryKey: ['approvals', 'quote', quoteId] });
      // The quote itself: a rejection sends it back to draft, and a builder
      // still showing pending-approval is one somebody presses Send on again.
      await queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
    },
  });

  if (approvals.isPending) return <Skeleton className="h-block-xs w-full" />;
  if (approvals.isError) return null;

  const { pending, history } = approvals.data;
  const decided = history.filter((entry) => entry.status !== 'pending');
  if (!pending && decided.length === 0) return null;

  const mine = pending?.requestedBy === session.user.id;
  const mayDecide = can('approval.decide') || can('discount.approve');

  return (
    <div data-region="approval" className="flex flex-col gap-2">
      {pending ? (
        <div
          data-approval-pending={pending.requestId}
          className="flex flex-col gap-2 rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text"
        >
          <span>
            {t('approval.pending', {
              values: {
                percent: pending.detail.discountPercent ?? '',
                cap: pending.detail.capPercent ?? '',
              },
            })}{' '}
            {t('approval.pending.by', { values: { name: pending.requestedByName ?? '' } })}
          </span>

          {/* Why they cannot decide, rather than an absent button. */}
          {mine ? (
            <span data-approval-own className="text-meta">
              {t('approval.own')}
            </span>
          ) : mayDecide ? (
            <div className="flex flex-wrap items-end gap-2">
              <input
                data-approval-comment
                aria-label={t('approval.reason')}
                placeholder={t('approval.reason')}
                className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
              <Button
                intent="primary"
                size="small"
                data-approval-approve={pending.requestId}
                onClick={() => decide.mutate({ requestId: pending.requestId, approve: true })}
              >
                {t('approval.approve')}
              </Button>
              <Button
                intent="secondary"
                size="small"
                data-approval-reject={pending.requestId}
                onClick={() => decide.mutate({ requestId: pending.requestId, approve: false })}
              >
                {t('approval.reject')}
              </Button>
            </div>
          ) : null}

          {decide.error ? (
            <span data-approval-refusal className="text-meta text-danger-text">
              {decide.error.message}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* What was decided before, and by whom. */}
      {decided.map((entry) => (
        <p
          key={entry.requestId}
          data-approval-decided={entry.requestId}
          className="text-meta text-ink-meta"
        >
          {entry.status === 'approved'
            ? t('approval.approved', {
                values: {
                  name: entry.decidedByName ?? '',
                  percent: entry.detail.discountPercent ?? '',
                },
              })
            : t('approval.rejected', {
                values: { name: entry.decidedByName ?? '', comment: entry.comment ?? '' },
              })}
        </p>
      ))}
    </div>
  );
}
